/**
 * IDF Validation Service
 *
 * Provides real-time validation markers in Monaco editor using the IDD schema:
 * - Required field validation
 * - Type validation (real, integer)
 * - Range validation (min/max)
 * - Choice validation
 * - Reference validation (check if referenced object exists)
 * - Duplicate name detection
 */

import type * as Monaco from 'monaco-editor';
import type { IDDSchema, IDDObjectType, IDDField } from '../idd/types';
import type { IDFDocument, IDFObject } from '../parser/types';
import { IDF_LANGUAGE_ID } from './idf-language';

/**
 * Validation marker with severity and message
 */
interface ValidationMarker {
  severity: 'error' | 'warning' | 'info';
  message: string;
  startLine: number;
  endLine: number;
  startColumn?: number;
  endColumn?: number;
}

/**
 * Validate a single field and return markers
 */
function validateField(
  field: { value: string } | undefined,
  fieldInfo: IDDField,
  fieldIndex: number,
  object: IDFObject,
  document: IDFDocument,
  schema: IDDSchema
): ValidationMarker[] {
  const markers: ValidationMarker[] = [];
  const value = field?.value?.toString().trim() ?? '';

  // Calculate approximate line for this field
  const fieldLine = (object.sourceLocation?.startLine ?? 1) + fieldIndex + 1;

  // Required field validation
  if (fieldInfo.required && !value) {
    markers.push({
      severity: 'error',
      message: `Required field "${fieldInfo.name || fieldInfo.id}" is empty`,
      startLine: fieldLine,
      endLine: fieldLine,
    });
    return markers; // Skip further validation if required field is empty
  }

  if (!value) return markers;

  // Type validation for numeric fields
  if (fieldInfo.type === 'real' || fieldInfo.type === 'integer') {
    const lowerValue = value.toLowerCase();
    if (lowerValue !== 'autocalculate' && lowerValue !== 'autosize') {
      const num = parseFloat(value);
      if (isNaN(num)) {
        markers.push({
          severity: 'error',
          message: `Expected ${fieldInfo.type === 'integer' ? 'an integer' : 'a number'} for "${fieldInfo.name || fieldInfo.id}"`,
          startLine: fieldLine,
          endLine: fieldLine,
        });
      } else if (fieldInfo.type === 'integer' && !Number.isInteger(num)) {
        markers.push({
          severity: 'error',
          message: `Expected an integer for "${fieldInfo.name || fieldInfo.id}", got decimal`,
          startLine: fieldLine,
          endLine: fieldLine,
        });
      } else {
        // Range validation
        if (fieldInfo.minimum !== undefined) {
          const isValid = fieldInfo.exclusiveMinimum
            ? num > fieldInfo.minimum
            : num >= fieldInfo.minimum;
          if (!isValid) {
            markers.push({
              severity: 'warning',
              message: `Value ${num} is below minimum ${fieldInfo.minimum} for "${fieldInfo.name || fieldInfo.id}"`,
              startLine: fieldLine,
              endLine: fieldLine,
            });
          }
        }
        if (fieldInfo.maximum !== undefined) {
          const isValid = fieldInfo.exclusiveMaximum
            ? num < fieldInfo.maximum
            : num <= fieldInfo.maximum;
          if (!isValid) {
            markers.push({
              severity: 'warning',
              message: `Value ${num} is above maximum ${fieldInfo.maximum} for "${fieldInfo.name || fieldInfo.id}"`,
              startLine: fieldLine,
              endLine: fieldLine,
            });
          }
        }
      }
    }
  }

  // Choice validation
  if (fieldInfo.choices && fieldInfo.choices.length > 0) {
    const normalizedValue = value.toLowerCase();
    const validChoice = fieldInfo.choices.some(
      (c) => c.toLowerCase() === normalizedValue
    );
    const allowsAuto =
      (fieldInfo.autocalculatable && normalizedValue === 'autocalculate') ||
      (fieldInfo.autosizable && normalizedValue === 'autosize');

    if (!validChoice && !allowsAuto) {
      markers.push({
        severity: 'error',
        message: `Invalid choice "${value}" for "${fieldInfo.name || fieldInfo.id}". Valid options: ${fieldInfo.choices.slice(0, 5).join(', ')}${fieldInfo.choices.length > 5 ? '...' : ''}`,
        startLine: fieldLine,
        endLine: fieldLine,
      });
    }
  }

  // Reference validation
  if (fieldInfo.objectList) {
    const referencedTypes = schema.referenceLists.get(fieldInfo.objectList);
    if (referencedTypes && value) {
      const exists = document.objects.some((obj) => {
        const classMatches =
          referencedTypes.has(obj.className) ||
          referencedTypes.has(obj.className.toLowerCase());
        if (!classMatches) return false;
        const objName = obj.fields[0]?.value?.toLowerCase() ?? '';
        return objName === value.toLowerCase();
      });

      if (!exists) {
        markers.push({
          severity: 'warning',
          message: `Referenced object "${value}" not found in document`,
          startLine: fieldLine,
          endLine: fieldLine,
        });
      }
    }
  }

  // Deprecated field warning
  if (fieldInfo.deprecated) {
    markers.push({
      severity: 'info',
      message: `Field "${fieldInfo.name || fieldInfo.id}" is deprecated`,
      startLine: fieldLine,
      endLine: fieldLine,
    });
  }

  return markers;
}

/**
 * Validate a single object and return markers
 */
function validateObject(
  object: IDFObject,
  classInfo: IDDObjectType | undefined,
  document: IDFDocument,
  schema: IDDSchema
): ValidationMarker[] {
  const markers: ValidationMarker[] = [];
  const startLine = object.sourceLocation?.startLine ?? 1;

  if (!classInfo) {
    markers.push({
      severity: 'warning',
      message: `Unknown object class: ${object.className}`,
      startLine,
      endLine: startLine,
    });
    return markers;
  }

  // Validate each field
  for (let i = 0; i < Math.max(object.fields.length, classInfo.fields.length); i++) {
    const field = object.fields[i];
    const fieldInfo = classInfo.fields[i];
    if (fieldInfo) {
      const fieldMarkers = validateField(field, fieldInfo, i, object, document, schema);
      markers.push(...fieldMarkers);
    }
  }

  // Check for duplicate names (first field is usually the name)
  if (classInfo.fields[0]?.references && classInfo.fields[0].references.length > 0) {
    const name = object.fields[0]?.value?.toLowerCase();
    if (name) {
      const duplicates = document.objects.filter(
        (o) =>
          o.className.toLowerCase() === object.className.toLowerCase() &&
          o.fields[0]?.value?.toLowerCase() === name &&
          o.id !== object.id
      );
      if (duplicates.length > 0) {
        markers.push({
          severity: 'error',
          message: `Duplicate name "${object.fields[0]?.value}" in ${object.className}`,
          startLine,
          endLine: startLine,
        });
      }
    }
  }

  // Check minimum fields requirement
  const filledFields = (object.fields ?? []).filter((f) => f.value.trim() !== '').length;
  if (filledFields < classInfo.minFields) {
    markers.push({
      severity: 'warning',
      message: `Object has ${filledFields} fields, minimum is ${classInfo.minFields}`,
      startLine,
      endLine: startLine,
    });
  }

  // Obsolete object warning
  if (classInfo.isObsolete) {
    markers.push({
      severity: 'warning',
      message: `${object.className} is obsolete, consider using a newer alternative`,
      startLine,
      endLine: startLine,
    });
  }

  return markers;
}

/**
 * Validate entire document and return all markers
 */
export function validateDocument(
  document: IDFDocument | null,
  schema: IDDSchema | null
): ValidationMarker[] {
  if (!document || !schema) return [];

  const markers: ValidationMarker[] = [];

  for (const object of document.objects) {
    const classInfo = schema.objectTypes.get(object.className.toLowerCase());
    const objectMarkers = validateObject(object, classInfo, document, schema);
    markers.push(...objectMarkers);
  }

  return markers;
}

/**
 * Update Monaco editor markers with validation results
 */
export function updateValidationMarkers(
  monaco: typeof Monaco,
  editor: Monaco.editor.IStandaloneCodeEditor,
  document: IDFDocument | null,
  schema: IDDSchema | null
): void {
  const model = editor.getModel();
  if (!model) return;

  const validationMarkers = validateDocument(document, schema);

  // Convert to Monaco markers
  const monacoMarkers: Monaco.editor.IMarkerData[] = validationMarkers.map((marker) => ({
    severity:
      marker.severity === 'error'
        ? monaco.MarkerSeverity.Error
        : marker.severity === 'warning'
          ? monaco.MarkerSeverity.Warning
          : monaco.MarkerSeverity.Info,
    message: marker.message,
    startLineNumber: marker.startLine,
    startColumn: marker.startColumn ?? 1,
    endLineNumber: marker.endLine,
    endColumn: marker.endColumn ?? model.getLineMaxColumn(marker.endLine),
    source: 'idf-validation',
  }));

  // Set markers (add to existing parse errors)
  monaco.editor.setModelMarkers(model, 'idf-validation', monacoMarkers);
}

/**
 * Register code lens provider for object validation status and reference counts
 */
export function registerCodeLensProvider(
  monaco: typeof Monaco,
  getDocument: () => IDFDocument | null,
  getSchema: () => IDDSchema | null,
  isContentSynced?: () => boolean
): Monaco.IDisposable {
  return monaco.languages.registerCodeLensProvider(IDF_LANGUAGE_ID, {
    provideCodeLenses(_model): Monaco.languages.CodeLensList {
      const document = getDocument();
      const schema = getSchema();
      if (!document || !schema) return { lenses: [], dispose: () => {} };

      // Skip CodeLens if content is out of sync (user is mid-edit)
      // This prevents showing lenses at wrong line positions
      if (isContentSynced && !isContentSynced()) {
        return { lenses: [], dispose: () => {} };
      }

      const lenses: Monaco.languages.CodeLens[] = [];

      for (const object of document.objects) {
        if (!object.sourceLocation) continue;

        const startLine = object.sourceLocation.startLine;
        const classInfo = schema.objectTypes.get(object.className.toLowerCase());

        // Count validation issues
        const objectMarkers = classInfo
          ? validateObject(object, classInfo, document, schema)
          : [];
        const errorCount = objectMarkers.filter((m) => m.severity === 'error').length;
        const warningCount = objectMarkers.filter((m) => m.severity === 'warning').length;

        // Count references to this object
        const objectName = object.fields[0]?.value;
        let refCount = 0;
        if (objectName) {
          for (const otherObj of document.objects) {
            if (otherObj.id === object.id) continue;
            const otherClassInfo = schema.objectTypes.get(otherObj.className.toLowerCase());
            if (!otherClassInfo) continue;

            for (let i = 0; i < otherObj.fields.length; i++) {
              const field = otherObj.fields[i];
              const fieldInfo = otherClassInfo.fields[i];
              if (fieldInfo?.objectList && field?.value?.toLowerCase() === objectName.toLowerCase()) {
                refCount++;
              }
            }
          }
        }

        // Create lens text
        const parts: string[] = [];
        if (errorCount > 0) parts.push(`${errorCount} error${errorCount !== 1 ? 's' : ''}`);
        if (warningCount > 0) parts.push(`${warningCount} warning${warningCount !== 1 ? 's' : ''}`);
        if (refCount > 0) parts.push(`${refCount} reference${refCount !== 1 ? 's' : ''}`);
        if (parts.length === 0) parts.push('valid');

        lenses.push({
          range: {
            startLineNumber: startLine,
            startColumn: 1,
            endLineNumber: startLine,
            endColumn: 1,
          },
          command: {
            id: '',
            title: parts.join(' | '),
          },
        });
      }

      return { lenses, dispose: () => {} };
    },
  });
}

/**
 * Register definition provider for go-to-definition on object references
 */
export function registerDefinitionProvider(
  monaco: typeof Monaco,
  getDocument: () => IDFDocument | null,
  getSchema: () => IDDSchema | null
): Monaco.IDisposable {
  return monaco.languages.registerDefinitionProvider(IDF_LANGUAGE_ID, {
    provideDefinition(model, position): Monaco.languages.Definition | null {
      const document = getDocument();
      const schema = getSchema();
      if (!document || !schema) return null;

      // Get the word at the current position
      const word = model.getWordAtPosition(position);
      if (!word) return null;

      const searchName = word.word.toLowerCase();

      // Find object with this name
      for (const obj of document.objects) {
        const objName = obj.fields[0]?.value?.toLowerCase();
        if (objName === searchName && obj.sourceLocation) {
          return {
            uri: model.uri,
            range: {
              startLineNumber: obj.sourceLocation.startLine,
              startColumn: 1,
              endLineNumber: obj.sourceLocation.endLine,
              endColumn: model.getLineMaxColumn(obj.sourceLocation.endLine),
            },
          };
        }
      }

      return null;
    },
  });
}

/**
 * Register all validation-related providers
 */
export interface ValidationServiceDisposables {
  codeLens: Monaco.IDisposable;
  definition: Monaco.IDisposable;
}

export function registerValidationService(
  monaco: typeof Monaco,
  getDocument: () => IDFDocument | null,
  getSchema: () => IDDSchema | null,
  isContentSynced?: () => boolean
): ValidationServiceDisposables {
  return {
    codeLens: registerCodeLensProvider(monaco, getDocument, getSchema, isContentSynced),
    definition: registerDefinitionProvider(monaco, getDocument, getSchema),
  };
}
